import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Images, Plus, Trash2, Upload } from "lucide-react";
import { api, apiError, mediaUrl } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import {
  AdminBanner,
  AdminButton,
  AdminCard,
  AdminField,
  AdminPageHead,
  AdminSpinner,
  Drawer,
  adminInputClass,
} from "@/components/admin/AdminUi";
import MediaPicker from "@/components/admin/MediaPicker";

const BLANK_CATEGORY = {
  title: "",
  slug: "",
  description: "",
  cover_image: "",
  external_url: "",
  external_label: "",
  display_order: 0,
  enabled: true,
};

const categoryPayload = (row) => ({
  title: row.title,
  slug: row.slug || "",
  description: row.description || "",
  cover_image: row.cover_image || "",
  external_url: row.external_url || "",
  external_label: row.external_label || "",
  display_order: Number(row.display_order) || 0,
  enabled: Boolean(row.enabled),
});

const itemPayload = (row) => ({
  image_url: row.image_url,
  title: row.title || "",
  caption: row.caption || "",
  alt_text: row.alt_text || "",
  display_order: Number(row.display_order) || 0,
  enabled: Boolean(row.enabled),
});

export function Galleries() {
  const { can } = useAuth();
  const writable = can("content.write");
  const [categories, setCategories] = useState(null);
  const [activeId, setActiveId] = useState("");
  const [items, setItems] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [banner, setBanner] = useState({ status: "idle", message: "" });
  const [progress, setProgress] = useState("");
  const bulkRef = useRef(null);

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/galleries");
      setCategories(data || []);
      setActiveId((current) => current || (data?.[0]?.id ?? ""));
    } catch (e) {
      setCategories([]);
      setBanner({ status: "error", message: apiError(e) });
    }
  }, []);

  const loadItems = useCallback(async (categoryId) => {
    if (!categoryId) {
      setItems([]);
      return;
    }
    try {
      const { data } = await api.get(`/admin/galleries/${categoryId}/items`);
      setItems(data || []);
    } catch (e) {
      setItems([]);
      setBanner({ status: "error", message: apiError(e) });
    }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { setItems(null); loadItems(activeId); }, [activeId, loadItems]);

  const active = (categories || []).find((c) => c.id === activeId) || null;

  const saveCategory = async () => {
    try {
      if (editingCategory.id) await api.put(`/admin/galleries/${editingCategory.id}`, categoryPayload(editingCategory));
      else await api.post("/admin/galleries", categoryPayload(editingCategory));
      setBanner({ status: "ok", message: "Gallery saved." });
      setEditingCategory(null);
      loadCategories();
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  const patchCategory = async (row, changes) => {
    try {
      await api.put(`/admin/galleries/${row.id}`, categoryPayload({ ...row, ...changes }));
      loadCategories();
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  const deleteCategory = async (row) => {
    if (!window.confirm(`Delete the “${row.title}” gallery and all ${row.item_count} photograph(s) in it?`)) return;
    try {
      await api.delete(`/admin/galleries/${row.id}`);
      setBanner({ status: "ok", message: "Gallery deleted." });
      if (row.id === activeId) setActiveId("");
      loadCategories();
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  const saveItem = async () => {
    try {
      if (editingItem.id) await api.put(`/admin/gallery-items/${editingItem.id}`, itemPayload(editingItem));
      else await api.post(`/admin/galleries/${activeId}/items`, itemPayload(editingItem));
      setBanner({ status: "ok", message: "Photograph saved." });
      setEditingItem(null);
      loadItems(activeId);
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  const patchItem = async (row, changes) => {
    try {
      await api.put(`/admin/gallery-items/${row.id}`, itemPayload({ ...row, ...changes }));
      loadItems(activeId);
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  const deleteItem = async (row) => {
    if (!window.confirm("Delete this photograph from the gallery?")) return;
    try {
      await api.delete(`/admin/gallery-items/${row.id}`);
      loadItems(activeId);
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  const moveItem = async (index, dir) => {
    const target = index + dir;
    if (!items || target < 0 || target >= items.length) return;
    const a = items[index];
    const b = items[target];
    try {
      await api.put(`/admin/gallery-items/${a.id}`, itemPayload({ ...a, display_order: b.display_order ?? target }));
      await api.put(`/admin/gallery-items/${b.id}`, itemPayload({ ...b, display_order: a.display_order ?? index }));
      loadItems(activeId);
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  const moveItemToCategory = async (row, categoryId) => {
    if (!categoryId || categoryId === row.category_id) return;
    try {
      await api.patch(`/admin/gallery-items/${row.id}/category`, { category_id: categoryId });
      setBanner({ status: "ok", message: "Photograph moved to another gallery." });
      loadItems(activeId);
      loadCategories();
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  const bulkUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length || !activeId) return;
    let order = (items || []).reduce((max, row) => Math.max(max, row.display_order ?? 0), 0);
    let added = 0;
    try {
      for (let i = 0; i < files.length; i += 1) {
        setProgress(`Uploading ${i + 1} of ${files.length}…`);
        const body = new FormData();
        body.append("file", files[i]);
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.post("/admin/media", body, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        order += 1;
        // eslint-disable-next-line no-await-in-loop
        await api.post(`/admin/galleries/${activeId}/items`, {
          image_url: `/api/media/${data.id}/file`,
          title: "",
          caption: "",
          alt_text: "",
          display_order: order,
          enabled: true,
        });
        added += 1;
      }
      setBanner({ status: "ok", message: `${added} photograph(s) uploaded and published.` });
    } catch (e) {
      setBanner({ status: "error", message: `${apiError(e)} (${added} uploaded before the error)` });
    } finally {
      setProgress("");
      if (bulkRef.current) bulkRef.current.value = "";
      loadItems(activeId);
      loadCategories();
    }
  };

  const setCat = (k) => (e) => setEditingCategory((f) => ({ ...f, [k]: e.target.value }));
  const setItem = (k) => (e) => setEditingItem((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="cms-galleries">
      <Seo title="Galleries | Bahamas Kidney Association CMS" noindex />
      <AdminPageHead
        title="Galleries"
        subtitle="Photo galleries shown on the public /gallery page. Each gallery has its own cover image, description and photographs."
      >
        {writable && (
          <AdminButton
            variant="emerald"
            testId="cms-gallery-add-category"
            onClick={() =>
              setEditingCategory({
                ...BLANK_CATEGORY,
                display_order: (categories || []).reduce((m, r) => Math.max(m, r.display_order ?? 0), 0) + 1,
              })
            }
          >
            <Plus className="h-4 w-4" /> Add Gallery
          </AdminButton>
        )}
      </AdminPageHead>

      <AdminBanner
        status={banner.status}
        message={banner.message}
        onDismiss={() => setBanner({ status: "idle", message: "" })}
      />

      <AdminCard className="mb-6">
        {categories === null ? (
          <AdminSpinner />
        ) : categories.length === 0 ? (
          <div className="p-6 text-sm text-slate-500" data-testid="cms-galleries-empty">
            No galleries yet. Use “Add Gallery” to create one.
          </div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="cms-galleries-list">
            {categories.map((row) => (
              <div
                key={row.id}
                className={`flex flex-wrap items-center gap-3 p-4 ${row.id === activeId ? "bg-emerald-50/40" : ""}`}
                data-testid={`cms-gallery-row-${row.slug}`}
              >
                <div className="h-14 w-20 shrink-0 overflow-hidden rounded-xl bg-slate-100">
                  {row.cover_image ? (
                    <img src={mediaUrl(row.cover_image)} alt="" className="h-full w-full object-cover" loading="lazy" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-slate-300">
                      <Images className="h-5 w-5" />
                    </div>
                  )}
                </div>
                <div className="min-w-[180px] flex-1">
                  <p className="text-sm font-bold text-bka-navy">{row.title}</p>
                  <p className="text-xs text-slate-500">
                    /gallery/{row.slug} · {row.item_count} photo(s) · Order {row.display_order ?? 0} ·{" "}
                    {row.enabled ? "Visible" : "Hidden"}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <AdminButton
                    variant={row.id === activeId ? "primary" : "ghost"}
                    className="px-3 py-1.5 text-xs"
                    testId={`cms-gallery-select-${row.slug}`}
                    onClick={() => setActiveId(row.id)}
                  >
                    Manage Photos
                  </AdminButton>
                  {writable && (
                    <>
                      <AdminButton
                        variant="ghost"
                        className="px-3 py-1.5 text-xs"
                        testId={`cms-gallery-edit-${row.slug}`}
                        onClick={() => setEditingCategory(row)}
                      >
                        Edit
                      </AdminButton>
                      <AdminButton
                        variant="ghost"
                        className="px-3 py-1.5 text-xs"
                        testId={`cms-gallery-toggle-${row.slug}`}
                        onClick={() => patchCategory(row, { enabled: !row.enabled })}
                      >
                        {row.enabled ? "Hide" : "Show"}
                      </AdminButton>
                      <button
                        type="button"
                        onClick={() => deleteCategory(row)}
                        aria-label="Delete gallery"
                        data-testid={`cms-gallery-delete-${row.slug}`}
                        className="flex h-[30px] w-[30px] items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </AdminCard>

      {active && (
        <AdminCard>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-4">
            <div>
              <p className="font-display text-base font-bold text-bka-navy">Photographs — {active.title}</p>
              <p className="text-xs text-slate-500">{progress || `${items?.length ?? 0} photograph(s) in this gallery.`}</p>
            </div>
            {writable && (
              <div className="flex flex-wrap gap-2">
                <input
                  ref={bulkRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={bulkUpload}
                  className="hidden"
                  data-testid="cms-gallery-bulk-input"
                />
                <AdminButton
                  variant="ghost"
                  testId="cms-gallery-bulk-upload"
                  onClick={() => bulkRef.current?.click()}
                  disabled={Boolean(progress)}
                >
                  <Upload className="h-4 w-4" /> {progress ? "Uploading…" : "Upload Multiple Photos"}
                </AdminButton>
                <AdminButton
                  variant="emerald"
                  testId="cms-gallery-add-item"
                  onClick={() =>
                    setEditingItem({
                      image_url: "",
                      title: "",
                      caption: "",
                      alt_text: "",
                      enabled: true,
                      display_order: (items || []).reduce((m, r) => Math.max(m, r.display_order ?? 0), 0) + 1,
                    })
                  }
                >
                  <Plus className="h-4 w-4" /> Add Photo
                </AdminButton>
              </div>
            )}
          </div>

          {items === null ? (
            <AdminSpinner />
          ) : items.length === 0 ? (
            <div className="p-6 text-sm text-slate-500" data-testid="cms-gallery-items-empty">
              No photographs in this gallery yet. Use “Upload Multiple Photos” to add them in one go.
            </div>
          ) : (
            <div className="grid gap-4 p-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="cms-gallery-items">
              {items.map((row, index) => (
                <div
                  key={row.id}
                  className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
                  data-testid={`cms-gallery-item-${row.id}`}
                >
                  <div className="relative h-40 bg-slate-100">
                    <img
                      src={mediaUrl(row.image_url)}
                      alt={row.alt_text || "Gallery photograph"}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                    <span
                      className={`absolute right-2 top-2 rounded-full px-2.5 py-1 text-[11px] font-extrabold uppercase ${
                        row.enabled ? "bg-emerald-600 text-white" : "bg-slate-500 text-white"
                      }`}
                    >
                      {row.enabled ? "Visible" : "Hidden"}
                    </span>
                  </div>
                  <div className="space-y-3 p-4">
                    <p className="line-clamp-2 text-sm font-semibold text-bka-navy">
                      {row.title || row.caption || <span className="text-slate-400">No caption</span>}
                    </p>
                    <p className="text-xs text-slate-500">Order {row.display_order ?? 0}</p>
                    <div className="flex flex-wrap gap-1.5">
                      <AdminButton
                        variant="ghost"
                        className="px-3 py-1.5 text-xs"
                        testId={`cms-gallery-item-edit-${row.id}`}
                        onClick={() => setEditingItem(row)}
                      >
                        Edit
                      </AdminButton>
                      {writable && (
                        <>
                          <AdminButton
                            variant="ghost"
                            className="px-3 py-1.5 text-xs"
                            testId={`cms-gallery-item-toggle-${row.id}`}
                            onClick={() => patchItem(row, { enabled: !row.enabled })}
                          >
                            {row.enabled ? "Hide" : "Show"}
                          </AdminButton>
                          <button
                            type="button"
                            onClick={() => moveItem(index, -1)}
                            aria-label="Move earlier"
                            data-testid={`cms-gallery-item-up-${row.id}`}
                            className="flex h-[30px] w-[30px] items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-bka-emerald hover:text-bka-emerald"
                          >
                            <ArrowUp className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveItem(index, 1)}
                            aria-label="Move later"
                            data-testid={`cms-gallery-item-down-${row.id}`}
                            className="flex h-[30px] w-[30px] items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-bka-emerald hover:text-bka-emerald"
                          >
                            <ArrowDown className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteItem(row)}
                            aria-label="Delete photograph"
                            data-testid={`cms-gallery-item-delete-${row.id}`}
                            className="flex h-[30px] w-[30px] items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                    {writable && (categories || []).length > 1 && (
                      <select
                        value={row.category_id}
                        onChange={(e) => moveItemToCategory(row, e.target.value)}
                        data-testid={`cms-gallery-item-move-${row.id}`}
                        className={`${adminInputClass} text-xs`}
                        aria-label="Move photograph to another gallery"
                      >
                        {(categories || []).map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.id === row.category_id ? `In: ${c.title}` : `Move to: ${c.title}`}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </AdminCard>
      )}

      <Drawer
        open={Boolean(editingCategory)}
        onClose={() => setEditingCategory(null)}
        title={editingCategory?.id ? "Edit Gallery" : "Add Gallery"}
        testId="cms-gallery-category-drawer"
      >
        {editingCategory && (
          <div className="space-y-5">
            <AdminField label="Gallery Title" required htmlFor="gc-title">
              <input
                id="gc-title"
                data-testid="cms-gallery-category-title"
                value={editingCategory.title}
                onChange={setCat("title")}
                className={adminInputClass}
                placeholder="BKA Fun Run & Walk 2026"
              />
            </AdminField>
            <AdminField label="Web Address" htmlFor="gc-slug" hint="Leave empty to generate it from the title.">
              <input
                id="gc-slug"
                data-testid="cms-gallery-category-slug"
                value={editingCategory.slug || ""}
                onChange={setCat("slug")}
                className={adminInputClass}
                placeholder="bka-fun-run-walk-2026"
              />
            </AdminField>
            <AdminField label="Short Description" htmlFor="gc-desc">
              <textarea
                id="gc-desc"
                rows={3}
                data-testid="cms-gallery-category-description"
                value={editingCategory.description || ""}
                onChange={setCat("description")}
                className={adminInputClass}
              />
            </AdminField>
            <AdminField label="Cover Image">
              <MediaPicker
                kind="images"
                label=""
                value={editingCategory.cover_image}
                onChange={(url) => setEditingCategory((f) => ({ ...f, cover_image: url }))}
                buttonLabel="Choose or Upload Cover Image"
                testId="cms-gallery-category-cover"
              />
            </AdminField>
            <AdminField label="Display Order" htmlFor="gc-order" hint="Lower numbers appear first.">
              <input
                id="gc-order"
                type="number"
                data-testid="cms-gallery-category-order"
                value={editingCategory.display_order ?? 0}
                onChange={setCat("display_order")}
                className={adminInputClass}
              />
            </AdminField>
            <AdminField
              label="Photographer's Full Album Link"
              htmlFor="gc-external"
              hint="Optional. Shown as a button so visitors can view the complete album on the photographer's site."
            >
              <input
                id="gc-external"
                data-testid="cms-gallery-category-external-url"
                value={editingCategory.external_url || ""}
                onChange={setCat("external_url")}
                className={adminInputClass}
                placeholder="https://…"
              />
            </AdminField>
            <AdminField label="Link Button Text" htmlFor="gc-external-label" hint="Defaults to “View the Full Album”.">
              <input
                id="gc-external-label"
                data-testid="cms-gallery-category-external-label"
                value={editingCategory.external_label || ""}
                onChange={setCat("external_label")}
                className={adminInputClass}
                placeholder="View the Full Album"
              />
            </AdminField>
            <label className="flex items-center gap-3 text-sm font-semibold text-bka-navy">
              <input
                type="checkbox"
                checked={Boolean(editingCategory.enabled)}
                onChange={(e) => setEditingCategory((f) => ({ ...f, enabled: e.target.checked }))}
                data-testid="cms-gallery-category-enabled"
                className="h-4 w-4 rounded border-slate-300"
              />
              Visible on the public gallery page
            </label>
            <AdminButton
              variant="emerald"
              testId="cms-gallery-category-save"
              onClick={saveCategory}
              disabled={!editingCategory.title || editingCategory.title.trim().length < 2}
            >
              Save Gallery
            </AdminButton>
          </div>
        )}
      </Drawer>

      <Drawer
        open={Boolean(editingItem)}
        onClose={() => setEditingItem(null)}
        title={editingItem?.id ? "Edit Photograph" : "Add Photograph"}
        testId="cms-gallery-item-drawer"
      >
        {editingItem && (
          <div className="space-y-5">
            <AdminField label="Photograph" required>
              <MediaPicker
                kind="images"
                label=""
                value={editingItem.image_url}
                onChange={(url) => setEditingItem((f) => ({ ...f, image_url: url }))}
                buttonLabel="Choose or Upload Photograph"
                testId="cms-gallery-item-image"
              />
            </AdminField>
            <AdminField label="Title (optional)" htmlFor="gi-title">
              <input
                id="gi-title"
                data-testid="cms-gallery-item-title"
                value={editingItem.title || ""}
                onChange={setItem("title")}
                className={adminInputClass}
              />
            </AdminField>
            <AdminField label="Caption (optional)" htmlFor="gi-caption">
              <textarea
                id="gi-caption"
                rows={3}
                data-testid="cms-gallery-item-caption"
                value={editingItem.caption || ""}
                onChange={setItem("caption")}
                className={adminInputClass}
              />
            </AdminField>
            <AdminField label="Alt Text" htmlFor="gi-alt" hint="Describes the photo for screen readers.">
              <input
                id="gi-alt"
                data-testid="cms-gallery-item-alt"
                value={editingItem.alt_text || ""}
                onChange={setItem("alt_text")}
                className={adminInputClass}
              />
            </AdminField>
            <AdminField label="Display Order" htmlFor="gi-order" hint="Lower numbers appear first.">
              <input
                id="gi-order"
                type="number"
                data-testid="cms-gallery-item-order"
                value={editingItem.display_order ?? 0}
                onChange={setItem("display_order")}
                className={adminInputClass}
              />
            </AdminField>
            <label className="flex items-center gap-3 text-sm font-semibold text-bka-navy">
              <input
                type="checkbox"
                checked={Boolean(editingItem.enabled)}
                onChange={(e) => setEditingItem((f) => ({ ...f, enabled: e.target.checked }))}
                data-testid="cms-gallery-item-enabled"
                className="h-4 w-4 rounded border-slate-300"
              />
              Visible on the public gallery page
            </label>
            <AdminButton
              variant="emerald"
              testId="cms-gallery-item-save"
              onClick={saveItem}
              disabled={!editingItem.image_url}
            >
              Save Photograph
            </AdminButton>
          </div>
        )}
      </Drawer>
    </div>
  );
}

export default Galleries;
